'use client'

import type { CSSProperties } from 'react'
import { createPortal } from 'react-dom'

/**
 * Matches the copied `birdie-chip-fly` keyframes in `birdie-board.scss` (B7).
 * The shared panel's keyframe runs 0.3 s and its timeout clears the chip at
 * 350 ms (`ChipPlacementPanel.tsx:1423-1425`, `chip-placement.scss:2714`); the
 * 50 ms tail is what keeps the last frame from popping.
 */
export const FLYING_CHIP_LIFETIME_MS = 350
/** How long the tile keeps `--receiving` (shared panel clears at 800 ms). */
export const RECEIVING_PULSE_MS = 800

export interface FlyingChipSpec {
  id: string
  fromX: number
  fromY: number
  toX: number
  toY: number
  bg: string
  border: string
  amount: number
}

/**
 * `(prefers-reduced-motion: reduce)`, SSR- and jsdom-safe — the same guard the
 * count-up animations use (`src/hooks/useCountUp.ts:8-9`). Read at call time,
 * not cached, so a viewer who flips the OS setting mid-round is honoured on the
 * next placement.
 */
export function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

/**
 * When the chip is considered to have LANDED — what B7/B9 wait before showing
 * the tile's `--receiving` pulse, so the pulse and the chip stack appear on the
 * same frame the flight ends. Reduced motion lands it immediately, because
 * `FlyingChipLayer` never draws the flight in that mode.
 */
export function landingDelayMs(): number {
  return prefersReducedMotion() ? 0 : FLYING_CHIP_LIFETIME_MS
}

/**
 * Viewport coordinates for one flight. The origin is the active chip disc in the
 * bar; the target is the tile's GLYPH ROW (not the tile's top-right corner the
 * shared panel uses at `ChipPlacementPanel.tsx:1414-1415`) because the Birdie
 * chip stack sits on the glyphs. `BirdieTile` hands the row's rect straight to
 * `onPlace`, so nothing here has to know the tile's box.
 */
export function flightPath(
  origin: DOMRect,
  target: DOMRect
): { fromX: number; fromY: number; toX: number; toY: number } {
  return {
    fromX: origin.left + origin.width / 2,
    fromY: origin.top + origin.height / 2,
    toX: target.left + target.width / 2,
    toY: target.top + target.height / 2
  }
}

/**
 * A small copy of the shared panel's inline flying chip
 * (`ChipPlacementPanel.tsx:1399-1425` + the body portal at `:3602-3627`): same
 * CSS custom properties (`--fly-from-x/-y`, `--fly-to-x/-y`,
 * `chip-placement.scss:2702-2754`), same `position: fixed` portal to
 * `document.body` so no ancestor transform (the bottom panel is CSS-scaled) can
 * shift the flight.
 *
 * Renders nothing under `prefers-reduced-motion`: the chip lands instantly on
 * the tile instead of arcing to it.
 */
export function FlyingChipLayer({
  chips,
  label,
  scale = 1
}: Readonly<{
  chips: readonly FlyingChipSpec[]
  label: (amount: number) => string
  /**
   * The board's UI scale, `frameScale(frame)` (B14). The flight's endpoints are
   * `getBoundingClientRect()` values and are therefore already in viewport px,
   * but the disc itself is a fixed 32 px — on a scaled-down stage it would land
   * on a tile smaller than itself, and on a scaled-up one it would look
   * undersized. `--birdie-chip-k` scales the disc to match the board it lands
   * on; the keyframes multiply their own scale steps by it.
   */
  scale?: number
}>) {
  if (typeof document === 'undefined' || chips.length === 0 || prefersReducedMotion()) return null
  return createPortal(
    <>
      {chips.map((chip) => (
        <div
          className="birdie-flying"
          key={chip.id}
          style={
            {
              '--fly-from-x': `${chip.fromX}px`,
              '--fly-from-y': `${chip.fromY}px`,
              '--fly-to-x': `${chip.toX}px`,
              '--fly-to-y': `${chip.toY}px`,
              '--birdie-chip-k': scale,
              background: chip.bg,
              borderColor: chip.border
            } as CSSProperties
          }
        >
          <span className="birdie-flying__amount">{label(chip.amount)}</span>
        </div>
      ))}
    </>,
    document.body
  )
}
